import React from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl, MessageDescriptor } from 'react-intl';
import { useSelector } from 'react-redux';

import { StoreShape } from 'hellospa/redux/types';
import { TooltipUI, ArrowDirection } from 'hellospa/foundation/control/tooltip';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { SectionHeader } from '../header';
import AttachmentIcon from './icons/attachment';
import AccessCodeIcon from './icons/access-code';
import styles from './index.scss';
import messages from './intl';

const formatRecipientName = (recipient: types.Recipient) => {
  switch (recipient.type) {
    case types.RecipientTypes.Signer: {
      const roleText = recipient.role ? `${recipient.role.name} - ` : null;
      const emailText = recipient.email ? `(${recipient.email})` : null;
      return (
        <div className={styles.recipientRowItem}>
          {roleText && <b>{roleText}</b>}
          {recipient.name} {emailText}
        </div>
      );
    }
    case types.RecipientTypes.Role:
      return (
        <div className={styles.recipientRowItem}>{recipient.name}</div>
      );
    default:
      return null;
  }
};

type RecipientProps = {
  recipient: types.Recipient,
  index: number
};

const Recipient = ({ recipient, index }: RecipientProps) => {
  const { recipientOrder } = useSelector(selectors.getSettings);
  const attachmentCount = 'attachments' in recipient
    ? recipient.attachments.length
    : 0;
  const displayName = formatRecipientName(recipient);
  const accessCode = 'accessCode' in recipient ? recipient.accessCode : undefined;
  const smsAuthNumber = 'smsAuthNumber' in recipient ? recipient.smsAuthNumber : undefined;

  return (
    <div
      id={recipient.id}
      className={styles.recipientDisplay}
      data-qa-ref="recipients.display"
    >
      <div
        className={classNames(
          styles.order,
          styles.recipientDisplayItem,
          {
            [styles.recipientOrder]: recipientOrder,
          },
        )}
        data-qa-ref={`recipients.${index}.order`}
      >
        {recipientOrder && <b>{index + 1}</b>}
      </div>
      <div
        className={styles.recipientDisplayItem}
        data-qa-ref={`recipients.${index}.label`}
      >
        {displayName}
      </div>
      <div className={classNames(styles.recipientIconContainer)}>
        {attachmentCount > 0 && (
          <div className={classNames(styles.recipientDisplayIcon, styles.recipientDisplayItem)}>
            <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
              <FormattedMessage
                id=""
                description="On hover, this tooltip is displayed to explain the associated icon"
                values={{
                  count: attachmentCount,
                }}
                defaultMessage={'You are requesting {count} {count, plural, one {attachment} other {attachments}} from this signer'} />
            </TooltipUI>
            <AttachmentIcon />
          </div>
        )}
        {accessCode && (
          <div className={classNames(styles.recipientDisplayIcon, styles.recipientDisplayItem)}>
            <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
              <FormattedMessage
                id=""
                description="On hover, this tooltip is displayed to explain the associated icon"
                values={{
                  accessCode,
                }}
                defaultMessage={'Access code: {accessCode}'} />
            </TooltipUI>
            <AccessCodeIcon />
          </div>
        )}
        {smsAuthNumber && (
          <div className={classNames(styles.recipientDisplayIcon, styles.recipientDisplayItem)}>
            <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
              <FormattedMessage
                id=""
                description="On hover, this tooltip is displayed to explain the associated icon"
                values={{
                  smsAuthNumber,
                }}
                defaultMessage={'SMS Phone Number: {smsAuthNumber}'} />
            </TooltipUI>
            <AccessCodeIcon />
          </div>
        )}
      </div>
    </div>
  );
};

const Recipients = () => {
  const intl = useIntl();
  const isSignerRolesEnabled = useSelector(selectors.isSignerRolesReviewEnabled);
  const isSignersEnabled = useSelector(selectors.isSignersReviewEnabled);
  const recipientType = useSelector(selectors.getCCRecipientType);
  const roles = useSelector((state: StoreShape) => (
    selectors.getRecipientsOfType(state, types.RecipientTypes.Role)
  ));
  const signers = useSelector((state: StoreShape) => (
    selectors.getRecipientsOfType(state, types.RecipientTypes.Signer)
  ));
  const isBulkSend = useSelector(selectors.isBulkSend);
  const signerFile = useSelector(selectors.getBulkSendData).signerFile;

  let recipients: (types.Role | types.Signer)[];
  let header: MessageDescriptor;

  if (isSignerRolesEnabled) {
    recipients = roles;
    header = messages.headerSignerRoles;
  } else if (isSignersEnabled || isBulkSend) {
    // Filter out empty names, handles optional signers
    recipients = signers.filter((signer) => signer.name.length > 0);
    header = messages.headerSigners;
  } else {
    return null;
  }

  return (
    <div
      className={styles.recipientContainer}
      data-recipient-type={recipientType}
    >
      <SectionHeader text={intl.formatMessage(header)} />
      {isBulkSend && signerFile && (
        <div className={styles.recipientDisplay}>
          <span className={styles.recipientDisplayItem}>{signerFile.name}</span>
        </div>
      )}
      {!isBulkSend && recipients.map((recipient, index) => (
        <Recipient
          key={recipient.id}
          recipient={recipient}
          index={index} />
      ))}
    </div>
  );
};

export default Recipients;
